import React, { useState } from 'react';
import { Box, Container, Typography, TextField, Button, Paper, IconButton, Backdrop, CircularProgress } from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { createProduct } from '../api/products';

const ProductForm = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [product, setProduct] = useState({
        title: '',
        description: '',
        price: '',
        features: [''],
        image: null
    });
    const [imagePreview, setImagePreview] = useState(null);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        setProduct({ ...product, image: file });
        setImagePreview(URL.createObjectURL(file));
    };

    const addFeature = () => {
        setProduct({
            ...product,
            features: [...product.features, '']
        });
    };

    const removeFeature = (index) => {
        const newFeatures = product.features.filter((_, i) => i !== index);
        setProduct({
            ...product,
            features: newFeatures
        });
    };

    const updateFeature = (index, value) => {
        const newFeatures = [...product.features];
        newFeatures[index] = value;
        setProduct({
            ...product,
            features: newFeatures
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const formData = new FormData();
        formData.append('title', product.title);
        formData.append('description', product.description);
        formData.append('price', product.price);
        product.features.forEach((feature, index) => {
            formData.append(`features[${index}]`, feature);
        });
        if (product.image) {
            formData.append('image', product.image);
        }

        try {
            await createProduct(formData);
            navigate('/admin/products');
        } catch (error) {
            console.error('Error creating product:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Container maxWidth="md">
                <Box sx={{ mt: 4, mb: 4 }}>
                    <Typography variant="h4" gutterBottom>
                        Create New Product
                    </Typography>
                    <Paper sx={{ p: 3 }}>
                        <form onSubmit={handleSubmit}>
                            <TextField
                                fullWidth
                                label="Title"
                                margin="normal"
                                required
                                value={product.title}
                                onChange={(e) => setProduct({ ...product, title: e.target.value })}
                            />
                            <TextField
                                fullWidth
                                label="Description"
                                margin="normal"
                                required
                                multiline
                                rows={4}
                                value={product.description}
                                onChange={(e) => setProduct({ ...product, description: e.target.value })}
                            />
                            <TextField
                                fullWidth
                                label="Price"
                                margin="normal"
                                required
                                type="number"
                                value={product.price}
                                onChange={(e) => setProduct({ ...product, price: e.target.value })}
                            />
                            
                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                                Product Features
                            </Typography>
                            {product.features.map((feature, index) => (
                                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                    <TextField
                                        fullWidth
                                        label={`Feature ${index + 1}`}
                                        value={feature}
                                        onChange={(e) => updateFeature(index, e.target.value)}
                                    />
                                    <IconButton
                                        color="error"
                                        onClick={() => removeFeature(index)}
                                        disabled={product.features.length === 1}
                                    >
                                        <RemoveIcon />
                                    </IconButton>
                                </Box>
                            ))}
                            <Button
                                startIcon={<AddIcon />}
                                onClick={addFeature}
                                sx={{ mt: 1 }}
                            >
                                Add Feature
                            </Button>

                            <Box sx={{ mt: 2, mb: 2 }}>
                                <input
                                    accept="image/*"
                                    type="file"
                                    onChange={handleImageChange}
                                    style={{ display: 'none' }}
                                    id="image-upload"
                                />
                                <label htmlFor="image-upload">
                                    <Button variant="contained" component="span">
                                        Upload Image
                                    </Button>
                                </label>
                            </Box>
                            {imagePreview && (
                                <Box sx={{ mt: 2, mb: 2 }}>
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        style={{ maxWidth: '100%', maxHeight: 300 }}
                                    />
                                </Box>
                            )}
                            <Box sx={{ mt: 3 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Creating...' : 'Create Product'}
                                </Button>
                            </Box>
                        </form>
                    </Paper>
                </Box>
            </Container>

            <Backdrop
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                }}
                open={isLoading}
            >
                <CircularProgress color="inherit" size={60} />
                <Typography variant="h6">
                    Creating Product...
                </Typography>
            </Backdrop>
        </>
    );
};

export default ProductForm;
